import { imageNames } from "@/src/constants/imageConstants.js";
import AuditTrail from "@/src/components/audit_components/AuditTrails.vue";
import SequenceEmails from "@/src/views/sequence/emails/SequenceEmails.vue";
import SequenceLinkedinLogs from "@/src/views/sequence/linkedin_logs/SequenceLinkedinLogs.vue";
import SequenceContact from "@/src/views/sequence/contacts/SequenceContact.vue";
import SequenceSettings from "@/src/views/sequence/settings/SequenceSettings.vue";
import SequenceOverview from "@/src/views/sequence/overview/SequenceOverview.vue";
import SequenceAnalytics from "@/src/views/sequence/analytics/SequenceAnalytics.vue";
import { featureNames } from "./globalConstants";

export const sequenceTabs = {
  EMAILS: "emails",
  LINKEDIN_LOGS: "linkedin_logs",
  OVERVIEW: "overview",
  CONTACTS_LEADS: "contacts/leads",
  AUDIT_TRAIL: "audit_trail",
  ANALYTICS: "analytics",
  SETTINGS: "settings",
};

export const permissionOptions = [
  { value: "private", label: "Private to Owner" },
  { value: "team_view", label: "Team can view" },
  { value: "team_edit", label: "Team can view and edit" },
];

export const timeMap = [
  { label: "minutes", value: "MINUTES" },
  { label: "hours", value: "HOURS" },
  { label: "days", value: "DAYS" },
];

export const timingTypes = {
  IMMEDIATE: "IMMEDIATE",
  SCHEDULE: "SCHEDULE",
};

export const delayTimeUnits = {
  MINUTES: "MINUTES",
  HOURS: "HOURS",
  DAYS: "DAYS",
};

export const defaultTimes = {
  START_TIME: "9:00am",
  END_TIME: "5:30pm",
};

export const sequenceStepTypes = {
  PHONE_CALL: "PHONE_CALL",
  TASK: "TASK",
  EMAIL: "EMAIL",
  VOICE: "VOICE",
  QUALIFICATION: "QUALIFICATION",
  MARKET_RESEARCH: "MARKET_RESEARCH",
  NEXT_BEST_ACTION: "NEXT_BEST_ACTION",
  MULTI_THREADING: "MULTI_THREADING",
  LINKEDIN_MESSAGE: "LINKEDIN_MESSAGE",
  LINKEDIN_INVITE: "LINKEDIN_INVITE",
  LINKEDIN_INMAIL: "LINKEDIN_INMAIL",
  LINKEDIN: "LINKEDIN",
  WHATSAPP_MESSAGE: "WHATSAPP_MESSAGE",
};

export const sequenceAutomationTypes = {
  AUTOMATED: "AUTOMATED",
  TASK: "TASK",
  AI: "AI",
};

export const sequenceCreationSelectorOptions = [
  {
    id: "build_from_scratch",
    title: "Build from scratch",
    description: `Create a new ${featureNames.SEQUENCES} from ground up`,
    icon: imageNames.RENAME,
    alt: "build_from_scratch_icon",
  },
  {
    id: "choose_template",
    title: "Choose a Prebuilt Template",
    description: `Select a ${featureNames.SEQUENCES} template from our library`,
    icon: imageNames.DOCUMENT_GREY,
    alt: "choose_template_icon",
  },
];

export const dayNames = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export const tableHeaders = [
  { label: "Name", key: "name", width: 3, avatar: "profile_pic", view: true },
  {
    label: "Current Company",
    key: "current_company",
    width: 3,
    avatar: "logo_url",
    view: true,
  },
  { label: "Current Role", key: "current_role", width: 3 },
  { label: "Contact/Lead", key: "entity_type", width: 3 },
];

export const stepTypeConstants = {
  EMAIL: "EMAIL",
  PHONE: "PHONE",
  CALL: "CALL",
  LINKEDIN: "LINKEDIN",
  TASK: "TASK",
  QUALIFICATION: "QUALIFICATION",
  MARKET_RESEARCH: "MARKET_RESEARCH",
  MULTI_THREADING: "MULTI_THREADING",
  NEXT_BEST_ACTION: "NEXT_BEST_ACTION",
  VOICE: "VOICE",
  CONNECTION: "CONNECTION",
  INVITE: "INVITE",
  MESSAGE: "MESSAGE",
  INMAIL: "INMAIL",
};

export const stepTypeMapping = {
  EMAIL: {
    icon: imageNames.EMAIL_BLACK,
    iconClass: "size-4 invert",
    titles: {
      AI: "AI Email",
      AUTOMATED: "Automated Email",
      MANUAL: "Manual Email",
    },
    defaultContent:
      "Email content will be generated based on your configuration.",
  },
  PHONE: {
    icon: imageNames.CALL_BLACK,
    iconClass: "size-4 invert",
    titles: {
      DEFAULT: "Phone Call",
    },
    defaultContent: "Phone call task - manual action required",
  },
  CALL: {
    icon: imageNames.CALL_BLACK,
    iconClass: "size-4 invert",
    titles: {
      DEFAULT: "Phone Call",
    },
    defaultContent: "Phone call task - manual action required",
  },
  LINKEDIN: {
    icon: imageNames.LINKEDIN,
    iconClass: "size-4",
    titles: {
      CONNECTION: "LinkedIn - send connection request",
      INVITE: "LinkedIn - send connection request",
      MESSAGE: "LinkedIn Message",
      INMAIL: "LinkedIn InMail",
      DEFAULT: "LinkedIn",
    },
    defaultContent:
      "LinkedIn message content will be generated based on your configuration.",
  },
  TASK: {
    icon: imageNames.TASK,
    iconClass: "size-4",
    titles: {
      DEFAULT: "Manual Task",
    },
    defaultContent: "Manual task - action required",
  },
  QUALIFICATION: {
    icon: imageNames.TASK,
    iconClass: "size-4",
    titles: {
      DEFAULT: "AI Qualification",
    },
    defaultContent: "AI will qualify the lead based on your criteria.",
  },
  MARKET_RESEARCH: {
    icon: imageNames.TASK,
    iconClass: "size-4",
    titles: {
      DEFAULT: "AI Market Research",
    },
    defaultContent: "AI will conduct market research on the contact/company.",
  },
  MULTI_THREADING: {
    icon: imageNames.TASK,
    iconClass: "size-4",
    titles: {
      DEFAULT: "AI Multi Threading",
    },
    defaultContent:
      "AI will find and engage with multiple contacts within the same company.",
  },
  NEXT_BEST_ACTION: {
    icon: imageNames.TASK,
    iconClass: "size-4",
    titles: {
      DEFAULT: "AI Next Best Action",
    },
    defaultContent:
      "AI will determine the next best action to take with this contact.",
  },
  VOICE: {
    icon: imageNames.TASK,
    iconClass: "size-4",
    titles: {
      DEFAULT: "AI Voice",
    },
    defaultContent: "AI voice agent will handle the interaction.",
  },
};

export const sequenceTabsConfig = [
  {
    id: sequenceTabs.OVERVIEW,
    title: "Overview",
    component: SequenceOverview,
  },
  {
    id: sequenceTabs.CONTACTS_LEADS,
    title: "Contacts/Leads",
    component: SequenceContact,
  },
  {
    id: sequenceTabs.EMAILS,
    title: "Emails",
    component: SequenceEmails,
  },
  {
    id: sequenceTabs.LINKEDIN_LOGS,
    title: "LinkedIn",
    component: SequenceLinkedinLogs,
  },
  {
    id: sequenceTabs.AUDIT_TRAIL,
    title: "Audit Trail",
    component: AuditTrail,
  },
  {
    id: sequenceTabs.ANALYTICS,
    title: "Analytics",
    component: SequenceAnalytics,
  },
  {
    id: sequenceTabs.SETTINGS,
    title: "Settings",
    component: SequenceSettings,
  },
];

export const activeDaysOptions = [
  { value: "Sunday", label: "Sunday" },
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
];

export const mailboxTabTypes = {
  USERS: "Users",
  TEAMS: "Teams",
};

export const mailboxTabConfig = {
  tabs: [mailboxTabTypes.USERS, mailboxTabTypes.TEAMS],
  selectionModes: ["multi", "single"],
};

export const validationCategoryGroups = {
  NEW_PEOPLE: "new_people",
  REQUIRES_REVIEW: "requires_review",
  CANNOT_ADD: "cannot_add",
};

export const validationCategories = {
  complete: {
    key: "complete",
    group: validationCategoryGroups.NEW_PEOPLE,
    title: `New people in ${featureNames.SEQUENCES}`,
    icon: "checkCircle",
    defaultSelected: true,
    dataKey: "complete_entities",
  },

  ongoing_sequences: {
    key: "ongoing_sequences",
    group: validationCategoryGroups.REQUIRES_REVIEW,
    title: `people already in another ${featureNames.SEQUENCES}`,
    icon: "warning",
    defaultSelected: false,
    dataKey: "ongoing_sequence_entities",
  },
  completed_sequences: {
    key: "completed_sequences",
    group: validationCategoryGroups.REQUIRES_REVIEW,
    title: `people already in another ${featureNames.SEQUENCES} which is finished`,
    icon: "warning",
    defaultSelected: false,
    dataKey: "completed_sequence_entities",
  },
  empty_email: {
    key: "empty_email",
    group: validationCategoryGroups.REQUIRES_REVIEW,
    title: "people without an email address",
    icon: "warning",
    defaultSelected: false,
    dataKey: "empty_email_entities",
  },
  empty_linkedin: {
    key: "empty_linkedin",
    group: validationCategoryGroups.REQUIRES_REVIEW,
    title: "people without a LinkedIn Profile",
    icon: "warning",
    defaultSelected: false,
    dataKey: "empty_linkedin_entities",
  },
  multiple_invalid: {
    key: "multiple_invalid",
    group: validationCategoryGroups.REQUIRES_REVIEW,
    title: "Multiple Issues",
    description: "People with more than one potential issue",
    icon: "warning",
    defaultSelected: false,
    dataKey: "multiple_invalid_entities",
  },
  existing_sequences: {
    key: "existing_sequences",
    group: validationCategoryGroups.CANNOT_ADD,
    title: `people already in this ${featureNames.SEQUENCES}`,
    icon: "warning",
    defaultSelected: false,
    dataKey: "existing_sequence_entities",
  },
};

export const sequenceStatuses = {
  IN_ACTIVE: "IN_ACTIVE",
  ACTIVE: "ACTIVE",
  COMPLETE: "COMPLETE",
};

export const VALIDATION_CATEGORY_KEYS = {
  COMPLETE: "complete",
  ONGOING_SEQUENCES: "ongoing_sequences",
  COMPLETED_SEQUENCES: "completed_sequences",
  EXISTING_SEQUENCES: "existing_sequences",
  EMPTY_EMAIL: "empty_email",
  EMPTY_LINKEDIN: "empty_linkedin",
  MULTIPLE_INVALID: "multiple_invalid",
};

export const MULTIPLE_ISSUES_CATEGORY_KEYS = [
  VALIDATION_CATEGORY_KEYS.ONGOING_SEQUENCES,
  VALIDATION_CATEGORY_KEYS.COMPLETED_SEQUENCES,
  VALIDATION_CATEGORY_KEYS.EXISTING_SEQUENCES,
  VALIDATION_CATEGORY_KEYS.EMPTY_EMAIL,
  VALIDATION_CATEGORY_KEYS.EMPTY_LINKEDIN,
];

export const CATEGORY_ID_TO_KEY_MAPPING = {
  1: VALIDATION_CATEGORY_KEYS.EMPTY_LINKEDIN,
  2: VALIDATION_CATEGORY_KEYS.EMPTY_EMAIL,
  3: VALIDATION_CATEGORY_KEYS.COMPLETED_SEQUENCES,
  4: VALIDATION_CATEGORY_KEYS.ONGOING_SEQUENCES,
};

export const mailboxTableHeaders = [
  {
    label: "Sender Account",
    key: "email",
    width: 12,
  },
];

export const linkedinTableHeaders = [
  {
    label: "Sender Account",
    key: "account_name",
    width: 12,
  },
];

export const manualSelectionTableHeaders = [
  { label: "Name", key: "name", width: 3, avatar: "logo_url", view: true },
  {
    label: "Current Company",
    key: "current_company",
    width: 3,
    avatar: "logo_url",
    view: true,
  },
  { label: "Current Role", key: "current_role", width: 3 },
  { label: "Contact/Lead", key: "entity_type", width: 3 },
];

export const WARNING_MESSAGES = {
  USER: {
    BOTH_DISCONNECTED:
      "Email and LinkedIn are not available for this contact/lead. Respective steps will not be sent",
    EMAIL_ONLY_CONNECTED:
      "Linkedin profile is not available for this contact/lead. LinkedIn steps will not be sent",
    LINKEDIN_ONLY_CONNECTED:
      "Email is not available for this contact/lead. Email steps will not be sent",
    EMAIL_DISCONNECTED:
      "Email is not available for this contact/lead. Email steps will not be sent",
    LINKEDIN_DISCONNECTED:
      "Linkedin profile is not available for this contact/lead. LinkedIn steps will not be sent",
  },
  SENDER: {
    BOTH_DISCONNECTED:
      "Email and LinkedIn are not connected for this sender. Respective steps will not be sent",
    EMAIL_ONLY_CONNECTED:
      "LinkedIn is not connected for this sender. LinkedIn steps will not be sent",
    LINKEDIN_ONLY_CONNECTED:
      "Email is not connected for this sender. Email steps will not be sent",
    EMAIL_DISCONNECTED:
      "Email is not connected for this sender. Email steps will not be sent",
    LINKEDIN_DISCONNECTED:
      "LinkedIn is not connected for this sender. LinkedIn steps will not be sent",
  },
  USER_SELECTION: {
    BOTH_DISCONNECTED:
      "Email and LinkedIn are not available for this user. Respective steps will not be sent",
    EMAIL_ONLY_CONNECTED:
      "Linkedin profile is not available for this user. LinkedIn steps will not be sent",
    LINKEDIN_ONLY_CONNECTED:
      "Email is not available for this user. Email steps will not be sent",
    EMAIL_DISCONNECTED:
      "Email is not available for this user. Email steps will not be sent",
    LINKEDIN_DISCONNECTED:
      "Linkedin profile is not available for this user. LinkedIn steps will not be sent",
  },
};

export const validationTypes = {
  LIST: "LIST",
  MANUAL: "MANUAL",
  EMAIL: "EMAIL",
};

export const sequenceContactTableHeaders = [
  { label: "Name", key: "name", width: 3, avatar: "profile_pic", view: true },
  {
    label: "Current Company",
    key: "company_name",
    width: 3,
    avatar: "logo_url",
    view: true,
  },
  { label: "Current Role", key: "current_role", width: 2 },
  { label: "Current Step", key: "current_step", width: 2 },
  { label: "Status", key: "sequence_status", width: 2 },
  { label: "Sender", key: "sender_info", width: 3 },
];

export const groupConfigs = {
  [validationCategoryGroups.NEW_PEOPLE]: {
    icon: imageNames.CHECK_CIRCLE_2,
    iconAlt: "check_circle_2",
    textColor: "var(--text-color-positive)",
    actionText: "will be added",
  },
  [validationCategoryGroups.REQUIRES_REVIEW]: {
    icon: imageNames.CONTENT_PASTE_SEARCH,
    iconAlt: "content_paste_search",
    textColor: "var(--paused-icon)",
    actionText: "requires review",
  },
  [validationCategoryGroups.CANNOT_ADD]: {
    icon: imageNames.BLOCK_RED,
    iconAlt: "block_red",
    textColor: "var(--error-color)",
    actionText: "cannot be added",
  },
};

export const getValidationCategoryDataKeyMapping = () => {
  return Object.keys(validationCategories).reduce((mapping, key) => {
    mapping[key] = validationCategories[key].dataKey;
    return mapping;
  }, {});
};

export const LINKEDIN_MESSAGES = {
  FREE_ACCOUNT_LIMIT:
    "Messages sent via Free LinkedIn accounts are limited to 200 characters and steps will be skipped if exceeded",
};

export const LISTING_STATE_STORAGE_KEY = "sequences-listing-state";
